<script setup>
import GoogleAdsCampaignSearchTermViewTable from '@frontJs/components/tables/projectManagement/seo/GoogleAdsCampaignSearchTermViewTable.vue';
import GoogleSearchConsoleSnapshotTable from '@frontJs/components/tables/projectManagement/seo/GoogleSearchConsoleSnapshotTable.vue';
import PositionTable from '@frontJs/components/tables/projectManagement/seo/PositionTable.vue';
import TrackedKeywordTable from '@frontJs/components/tables/projectManagement/seo/TrackedKeywordTable.vue';
import PositionTrackingTable from '@frontJs/components/tables/projectManagement/seo/tenantReports/PositionTrackingTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { h, onMounted, ref } from 'vue';

const {
    tenant,
    websiteVariants,
    targetableGoogleWebsites,
    countries,
    googleSearchConsolePeriods,
} = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    websiteVariants: {
        type: Array,
        required: false,
    },
    targetableGoogleWebsites: {
        type: Array,
        required: false,
    },
    countries: {
        type: Array,
        required: false,
    },
    googleSearchConsolePeriods: {
        type: Array,
        required: false,
    },
});

const loading = ref(true);

const data = ref({
    customersWithFollowUp: 0,
    customersWithoutFollowUp: 0,
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.post(
            route('front.projectManagement.seo.postPositionTrackingCount'),
            {
                hasPositionTracking: true,
            },
        ),
        axios.post(
            route('front.projectManagement.seo.postPositionTrackingCount'),
            {
                hasPositionTracking: false,
            },
        ),
    ])
        .then(([customersWithFollowUpData, customersWithoutFollowUpData]) => {
            data.value.customersWithFollowUp =
                customersWithFollowUpData.data.count;
            data.value.customersWithoutFollowUp =
                customersWithoutFollowUpData.data.count;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    if (!tenant) {
        updateData();
    } else {
        loading.value = false;
    }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>
            Suivi positions / URL - SEO/GEO - Gestion de projet - Datakepler
        </title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - SEO/GEO - Suivi positions / URL
    </PageTitle>

    <TabLayout
        v-if="tenant"
        :tabs="{
            positions: {
                title: 'Positions',
                content: h(PositionTable, {
                    tenant,
                    websiteVariants,
                    targetableGoogleWebsites,
                }),
            },
            importantPositions: {
                title: 'Positions importantes',
                content: h(PositionTable, {
                    tenant,
                    websiteVariants,
                    targetableGoogleWebsites,
                    importantToFollow: true,
                }),
            },
            keywords: {
                title: 'Mots-clés',
                content: h(TrackedKeywordTable, { tenant, countries }),
            },
            seaKeywords: {
                title: 'Mots-clés SEA',
                content: h(GoogleAdsCampaignSearchTermViewTable, {
                    tenant,
                    countries,
                }),
            },
            searchConsoleKeywords: {
                title: 'Requêtes Search Console',
                content: h(GoogleSearchConsoleSnapshotTable, {
                    tenant,
                    websiteVariants,
                    periods: googleSearchConsolePeriods,
                    dimension: 'query',
                }),
            },
            searchConsolePages: {
                title: 'Pages Search Console',
                content: h(GoogleSearchConsoleSnapshotTable, {
                    tenant,
                    websiteVariants,
                    periods: googleSearchConsolePeriods,
                    dimension: 'page',
                }),
            },
        }"
    />

    <TabLayout
        v-else
        :tabs="{
            customersWithFollowUp: {
                title: 'Clients avec suivi',
                total: data.customersWithFollowUp,
                content: h(PositionTrackingTable, {
                    hasPositionTracking: true,
                }),
            },
            customersWithoutFollowUp: {
                title: 'Clients sans suivi',
                total: data.customersWithoutFollowUp,
                content: h(PositionTrackingTable, {
                    hasPositionTracking: false,
                }),
            },
        }"
        :loading="loading"
    />
</template>
